import type { AnyFieldApi } from "@tanstack/react-form";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { PillBtn } from "#/components/primitives";
import { Field } from "#/components/ui/field";
import { Input } from "#/components/ui/input";
import { isEmailVerified, register } from "#/lib/auth-client";
import { ANONYMOUS_SESSION } from "#/lib/medusa-types/auth";
import { notify } from "#/lib/notify";
import { ROUTES } from "#/lib/routes";
import { sessionKeys, sessionQueries } from "#/lib/useSession";

const searchSchema = z.object({
  redirect: z.string().optional(),
});

const AUTH_FIELD_CLASS = "h-14 px-6";

const FLATTEN_AUTH_ERRORS = false;

function isNetworkError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return /network|failed to fetch|connection|offline/i.test(err.message);
}

function flatRegisterError(err: unknown): string {
  if (isNetworkError(err)) return "Check your connection and try again.";
  return "Could not create account. Please try again.";
}

const registerSchema = z.object({
  firstName: z
    .string()
    .min(1, "Required")
    .max(120, "Must be 120 characters or fewer"),
  lastName: z
    .string()
    .min(1, "Required")
    .max(120, "Must be 120 characters or fewer"),
  email: z.string().email("Enter a valid email"),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .max(128, "Password must be 128 characters or fewer"),
  phone: z.string().max(40, "Must be 40 characters or fewer"),
});

type RegisterValues = z.infer<typeof registerSchema>;

function fieldErrorText(field: AnyFieldApi): string | null {
  if (!field.state.meta.isTouched) return null;
  const errs = field.state.meta.errors;
  if (errs.length === 0) return null;
  return errs
    .map((e) => (typeof e === "string" ? e : (e?.message ?? "Invalid")))
    .join(", ");
}

export const Route = createFileRoute("/register")({
  validateSearch: searchSchema,
  head: () => ({ meta: [{ title: "Create Account" }] }),
  beforeLoad: async ({ context, search }) => {
    const session = await context.queryClient.ensureQueryData(
      sessionQueries.current(),
    );
    if (session.customer && isEmailVerified(session.customer)) {
      throw redirect({ to: search.redirect ?? ROUTES.account });
    }
  },
  component: RegisterPage,
});

function RegisterPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (values: RegisterValues) =>
      register({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        phone: values.phone.length > 0 ? values.phone : undefined,
      }),
    onSuccess: () => {
      queryClient.setQueryData(sessionKeys.current, ANONYMOUS_SESSION);
      notify.success("Account created", {
        description: "Check your email to verify your account, then sign in.",
      });
      navigate({ to: ROUTES.login, search: { redirect: search.redirect } });
    },
    onError: (err) => {
      if (import.meta.env.DEV) {
        console.log("[register] error", err);
      }
      if (FLATTEN_AUTH_ERRORS) {
        setErrorMessage(flatRegisterError(err));
        return;
      }
      setErrorMessage(
        err instanceof Error
          ? err.message
          : "Could not create account. Please try again.",
      );
    },
  });

  const form = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      phone: "",
    } satisfies RegisterValues,
    validators: { onChange: registerSchema },
    onSubmit: ({ value }) => {
      setErrorMessage(null);
      return mutation.mutateAsync(value);
    },
  });

  return (
    <div data-screen-label="Register" className="pb-24">
      <section className="mx-auto w-full max-w-md px-6 pt-16 md:pt-24">
        <h1 className="m-0 mb-3 font-bold font-display text-4xl text-lp-green-dk leading-none tracking-tight">
          Create Your Account
        </h1>
        <p className="mt-2 mb-10 m-0 font-display text-lp-text">
          Save your favorites, track your orders, and check out faster.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="flex flex-col gap-5"
          noValidate
        >
          <div className="grid grid-cols-2 gap-4">
            <form.Field name="firstName">
              {(field) => {
                const errText = fieldErrorText(field);
                return (
                  <div>
                    <Field label="First Name">
                      {(id) => (
                        <Input
                          id={id}
                          type="text"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          autoComplete="given-name"
                          aria-invalid={errText !== null}
                          className={AUTH_FIELD_CLASS}
                        />
                      )}
                    </Field>
                    {errText && (
                      <p
                        role="alert"
                        className="mt-1 font-display text-destructive text-sm"
                      >
                        {errText}
                      </p>
                    )}
                  </div>
                );
              }}
            </form.Field>
            <form.Field name="lastName">
              {(field) => {
                const errText = fieldErrorText(field);
                return (
                  <div>
                    <Field label="Last Name">
                      {(id) => (
                        <Input
                          id={id}
                          type="text"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          autoComplete="family-name"
                          aria-invalid={errText !== null}
                          className={AUTH_FIELD_CLASS}
                        />
                      )}
                    </Field>
                    {errText && (
                      <p
                        role="alert"
                        className="mt-1 font-display text-destructive text-sm"
                      >
                        {errText}
                      </p>
                    )}
                  </div>
                );
              }}
            </form.Field>
          </div>

          <form.Field name="email">
            {(field) => {
              const errText = fieldErrorText(field);
              return (
                <div>
                  <Field label="Email">
                    {(id) => (
                      <Input
                        id={id}
                        type="email"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        autoComplete="email"
                        aria-invalid={errText !== null}
                        className={AUTH_FIELD_CLASS}
                      />
                    )}
                  </Field>
                  {errText && (
                    <p
                      role="alert"
                      className="mt-1 font-display text-destructive text-sm"
                    >
                      {errText}
                    </p>
                  )}
                </div>
              );
            }}
          </form.Field>

          <form.Field name="password">
            {(field) => {
              const errText = fieldErrorText(field);
              return (
                <div>
                  <Field label="Password">
                    {(id) => (
                      <Input
                        id={id}
                        type="password"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        autoComplete="new-password"
                        aria-invalid={errText !== null}
                        className={AUTH_FIELD_CLASS}
                      />
                    )}
                  </Field>
                  {errText && (
                    <p
                      role="alert"
                      className="mt-1 font-display text-destructive text-sm"
                    >
                      {errText}
                    </p>
                  )}
                </div>
              );
            }}
          </form.Field>

          <form.Field name="phone">
            {(field) => {
              const errText = fieldErrorText(field);
              return (
                <div>
                  <Field label="Phone (optional)">
                    {(id) => (
                      <Input
                        id={id}
                        type="tel"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        autoComplete="tel"
                        aria-invalid={errText !== null}
                        className={AUTH_FIELD_CLASS}
                      />
                    )}
                  </Field>
                  {errText && (
                    <p
                      role="alert"
                      className="mt-1 font-display text-destructive text-sm"
                    >
                      {errText}
                    </p>
                  )}
                </div>
              );
            }}
          </form.Field>

          {errorMessage && (
            <div className="font-display text-destructive text-sm">
              {errorMessage}
            </div>
          )}

          <div className="mt-2 flex justify-end">
            <form.Subscribe selector={(s) => [s.canSubmit, s.isSubmitting]}>
              {([canSubmit, isSubmitting]) => (
                <PillBtn
                  type="submit"
                  variant="green"
                  size="sm"
                  disabled={!canSubmit || isSubmitting || mutation.isPending}
                >
                  {mutation.isPending || isSubmitting
                    ? "Creating…"
                    : "Create Account"}
                </PillBtn>
              )}
            </form.Subscribe>
          </div>
        </form>

        <div className="mt-10 font-display text-lp-text">
          Already have an account?{" "}
          <Link
            to={ROUTES.login}
            search={{ redirect: search.redirect }}
            className="text-lp-green-dk underline hover:text-lp-green-mid"
          >
            Sign in
          </Link>
        </div>
      </section>
    </div>
  );
}
